'use strict';

const User = require('../models/User');
const pinterestUtils = require('../utils/social/pinterest');
const {NAME_PINTEREST} = require('bunsen/models/identity/SocialNetwork');
const {localStorage} = require('dibs-browser-storage');
const gaTracking = require('dibs-tracking');
const LOCAL_STORAGE_NAMESPACE = 'pinterest';
const LOCAL_STORAGE_BANNER = 'connectAccountsBanner';
const LOCAL_STORAGE_MODAL = 'connectAccountsModal';
const IMPRESSION_COUNT = 3;

// Actions
const socialNetworkActions = require('./socialNetworkActions');
const userSocialNetworkActions = require('../../user/actions/userSocialNetworkActions');

// Stores
const PinterestStore = require('../stores/PinterestStore');
const SocialNetworkStore = require('../stores/SocialNetworkStore');
const UserSocialNetworkStore = require('../../user/stores/UserSocialNetworkStore');

const getPinterestLocalStorageData = () => {
    return localStorage.getItem(LOCAL_STORAGE_NAMESPACE) || {};
};

const setPinterestLocalStorageData = (data) => {
    const pinterest = getPinterestLocalStorageData();
    localStorage.setItem(LOCAL_STORAGE_NAMESPACE, Object.assign(pinterest, data));
};

const pinterestActions = {

    _canShowPinterestBanner(actionContext) {
        return pinterestActions.canShowConnectAccountPromotion(actionContext, {
            namespace: LOCAL_STORAGE_BANNER
        });
    },

    _canShowConnectAccountsModal(actionContext) {
        return pinterestActions.canShowConnectAccountPromotion(actionContext, {
            namespace: LOCAL_STORAGE_MODAL
        });
    },

    _increaseConnectAccountsModalShowCount() {
        let {connectAccountsModal} = getPinterestLocalStorageData();
        connectAccountsModal += 1;
        setPinterestLocalStorageData({connectAccountsModal});
    },

    _increaseBannerShowCount() {
        let {connectAccountsBanner} = getPinterestLocalStorageData();
        connectAccountsBanner += 1;
        setPinterestLocalStorageData({connectAccountsBanner});
    },

    canShowConnectAccountPromotion(actionContext, {namespace}) {
        const userSocialNetworkStore = actionContext.getStore(UserSocialNetworkStore);
        const socialNetworks = userSocialNetworkStore.getSocialNetworks(NAME_PINTEREST);

        const socialNetworkStore = actionContext.getStore(SocialNetworkStore);
        const pinterest = socialNetworkStore.getSocialNetwork(NAME_PINTEREST);

        if (!User.loggedIn()) {
            return Promise.reject();
        }

        // Pinterest is not accessible
        if (!pinterest) {
            return Promise.reject();
        }

        // Was ever connected before
        if (socialNetworks.length) {
            return Promise.reject();
        }

        // If namespace was not passed, then we do not need to check
        // impression count - resolving promise.
        if (!namespace) {
            return Promise.resolve();
        }

        let impressionCount = getPinterestLocalStorageData()[namespace];

        if (!impressionCount) {
            impressionCount = 0;
            setPinterestLocalStorageData({
                [namespace]: impressionCount
            });
            return Promise.resolve();
        }

        if (impressionCount < IMPRESSION_COUNT) {
            return Promise.resolve();
        } else {
            return Promise.reject();
        }
    },

    initApp(actionContext, payload, done) {
        actionContext
            .executeAction(socialNetworkActions.fetch)
            .then(() => {
                const store = actionContext.getStore(SocialNetworkStore);
                const pinterest = store.getSocialNetwork(NAME_PINTEREST);
                let promise;

                if (pinterest) {
                    const {clientId: appId} = pinterest.parameters;
                    promise = pinterestUtils.init({appId});
                } else {
                    promise = Promise.reject();
                }

                return promise;
            })
            .then(() => {
                return actionContext.executeAction(pinterestActions.setSession);
            })
            .then(() => done())
            .catch(error => done(error));
    },

    initAppAndGetBoards(actionContext, payload, done) {
        actionContext
            .executeAction(pinterestActions.initApp)
            .then(() => {
                return actionContext.executeAction(pinterestActions.getBoards);
            })
            .then(() => done())
            .catch(error => done(error));
    },

    setSessionOnLoginChange(actionContext, payload, done) {
        User.getCurrentUser().on('login:change', () => {
            actionContext
                .executeAction(pinterestActions.setSession)
                .catch(error => done(error));
        });

        done();
    },

    setSession(actionContext, payload, done) {
        Promise.resolve(User.load())
            .then(() => {
                const store = actionContext.getStore(UserSocialNetworkStore);
                const pinterest = store.getConnectedSocialNetwork(NAME_PINTEREST);
                let promise;

                if (pinterest) {
                    const {accessToken, scope} = pinterest.userPreferences;
                    promise = pinterestUtils.setSession({accessToken, scope});
                } else {
                    promise = Promise.reject(`User is not connected with ${NAME_PINTEREST} social network.`);
                }

                return promise;
            })
            .then(user => {
                actionContext.dispatch('pinterest:user:loaded', user);
                done();
            })
            .catch(() => done());
    },

    connectAccounts(actionContext, payload, done) {
        const {pinterestWindow, tracking} = payload;
        const socialNetworkStore = actionContext.getStore(SocialNetworkStore);
        const pinterest = socialNetworkStore.getSocialNetwork(NAME_PINTEREST);

        actionContext.executeAction(pinterestActions.hideConnectAccountsModal)
            .then(() => {
                const clientId = pinterest && pinterest.parameters.clientId;
                return pinterestUtils.login({pinterestWindow, clientId});
            })
            .then(session => {
                const name = NAME_PINTEREST;
                const data = {name, data: session};

                return actionContext.executeAction(userSocialNetworkActions.addSocialNetwork, data);
            })
            .then(() => {
                return actionContext.executeAction(pinterestActions.setSession);
            })
            .then(() => {
                return actionContext.executeAction(pinterestActions.showConnectBoardModal);
            })
            .then(() => {
                actionContext.executeAction(pinterestActions.track, tracking);
                done();
            })
            .catch(error => {
                pinterestUtils.logout();
                done(error);
            });
    },

    disconnectAccounts(actionContext, payload, done) {
        const {tracking} = payload;

        pinterestUtils.logout()
            .then(() => {
                const name = NAME_PINTEREST;
                return actionContext.executeAction(userSocialNetworkActions.removeSocialNetwork, {name});
            })
            .then(() => {
                actionContext.executeAction(pinterestActions.track, tracking);
                actionContext.dispatch('pinterest:disconnected');
                done();
            })
            .catch(error => done(error));
    },

    pin(actionContext, payload, done) {
        const {pinterestWindow, url, media, description} = payload;
        pinterestUtils.pin({pinterestWindow, url, media, description}).then(() => done());
    },

    getBoards(actionContext, payload, done) {
        pinterestUtils.getBoards()
            .then(boards => {
                actionContext.dispatch('pinterest:boards:loaded', boards);

                actionContext
                    .executeAction(pinterestActions.getConnectedBoard)
                    .then(() => done())
                    .catch(error => done(error));
            })
            .catch(error => done(error));
    },

    createBoard(actionContext, payload, done) {
        const {name} = payload;

        const pinterestStore = actionContext.getStore(PinterestStore);
        const existingBoard = pinterestStore.getBoardByName(name);

        if (existingBoard) {
            done(null, existingBoard);
        } else {
            pinterestUtils.createBoard(name)
                .then(board => {
                    actionContext.dispatch('pinterest:boards:created', board);
                    done(null, board);
                })
                .catch(error => done(error));
        }
    },

    createAndConnectBoard(actionContext, payload, done) {
        const {name} = payload;

        actionContext
            .executeAction(pinterestActions.createBoard, {name})
            .then(board => {
                return actionContext.executeAction(pinterestActions.connectBoard, board);
            })
            .then(() => done())
            .catch(error => {
                actionContext.dispatch('pinterest:connectBoardModal:error');
                done(error);
            });
    },

    getConnectedBoard(actionContext, payload, done) {
        const userSocialNetworkStore = actionContext.getStore(UserSocialNetworkStore);
        const pinterest = userSocialNetworkStore.getConnectedSocialNetwork(NAME_PINTEREST);

        const pinterestStore = actionContext.getStore(PinterestStore);
        const boards = pinterestStore.getBoards();

        if (pinterest && boards.length) {
            const connectedBoard = boards.find(board => {
                return board.urlName === pinterest.userPreferences.board;
            });

            if (connectedBoard) {
                actionContext.dispatch('pinterest:board:connected', connectedBoard);
            }
        }

        done();
    },

    connectBoard(actionContext, payload, done) {
        const {id} = payload;
        const store = actionContext.getStore(PinterestStore);
        const board = store.getBoard(id);

        if (board) {
            const userSocialNetworkStore = actionContext.getStore(UserSocialNetworkStore);
            const pinterest = userSocialNetworkStore.getConnectedSocialNetwork(NAME_PINTEREST);
            const data = Object.assign({}, pinterest.userPreferences, {
                board: board.urlName
            });

            actionContext
                .executeAction(userSocialNetworkActions.updateSocialNetwork, {
                    name: NAME_PINTEREST,
                    data: data
                })
                .then(() => {
                    actionContext.dispatch('pinterest:board:connected', board);
                    actionContext.executeAction(pinterestActions.hideConnectBoardModal);
                    done();
                })
                .catch(error => done(error));
        } else {
            done(`Board with ${id} was not found`);
        }
    },

    showConnectModalAfterPinAdded(actionContext, payload, done) {
        const {url, tracking} = payload;
        let pinCount = 0;

        pinterestActions
            ._canShowConnectAccountsModal(actionContext)
            .then(() => {
                pinterestUtils.getPinCount({url})
                    .then(count => {
                        pinCount = count;
                        return actionContext.executeAction(pinterestActions.pin, payload);
                    })
                    .then(() => {
                        return pinterestUtils.getPinCount({url});
                    })
                    .then(count => {
                        if (pinCount !== count) {
                            pinterestActions._increaseConnectAccountsModalShowCount();
                            actionContext.executeAction(pinterestActions.showConnectAccountsModal);
                            actionContext.executeAction(pinterestActions.track, tracking);
                        }
                        done();
                    });
            })
            .catch(() => {
                actionContext.executeAction(pinterestActions.pin, payload);
                done();
            });
    },


    /**
     * used in newPdp/components/Favorites/FavoritesSaveWidgetWrapperView.js to show pinterest banner
     * in save to portfolio widget.
     * @param  {Object}         payload
     * @param  {Object.model}   Backbone model to change attributes
     */
    showPinterestBanner(actionContext, payload, done) {
        const {callback, tracking} = payload;

        pinterestActions
            ._canShowPinterestBanner(actionContext)
            .then(() => {
                if (typeof callback === 'function') {
                    callback();
                    pinterestActions._increaseBannerShowCount();
                    actionContext.executeAction(pinterestActions.track, tracking);
                }
                done();
            })
            .catch(() => done());
    },

    // Sometimes pinterest pin count service does not work.
    // backup function if we decide to get rid of pin count check:
    // showConnectModalAfterPinAdded(actionContext, payload, done) {
    //     pinterestUtils.isLoggedIn()
    //         .then(() => {
    //             actionContext.executeAction(pinterestActions.pin, payload);
    //             done();
    //         })
    //         .catch(() => {
    //             actionContext
    //                 .executeAction(pinterestActions.pin, payload)
    //                 .then(() => {
    //                     pinterestActions
    //                         ._canShowConnectAccountsModal()
    //                         .then(() => {
    //                             actionContext.executeAction(pinterestActions.showConnectAccountsModal);
    //                             done();
    //                         })
    //                         .catch(() => done());
    //                 });
    //         });
    // },

    track(actionContext, payload, done) {
        const {category, action, label} = payload;

        if (category && action && label) {
            gaTracking.trackEvent({category, action, label});
        }

        done();
    },

    showConnectAccountsModal(actionContext, payload, done) {
        actionContext.dispatch('pinterest:connectAccountsModal:show');
        done();
    },

    hideConnectAccountsModal(actionContext, payload, done) {
        actionContext.dispatch('pinterest:connectAccountsModal:hide');
        done();
    },

    showConnectBoardModal(actionContext, payload, done) {
        actionContext.dispatch('pinterest:connectBoardModal:show');
        actionContext.executeAction(pinterestActions.getBoards)
            .then(() => done())
            .catch(() => done());
    },

    hideConnectBoardModal(actionContext, payload, done) {
        actionContext.dispatch('pinterest:connectBoardModal:hide');
        done();
    }

};

module.exports = pinterestActions;



// WEBPACK FOOTER //
// ./assets/js/global/actions/pinterestActions.es.js